/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';

import { Grid, Typography2 } from '@coursera/cds-core';
import type { IconProps } from '@coursera/cds-core';
import {
  BarChartOneFillIcon,
  BarChartThreeFillIcon,
  BarChartTwoFillIcon,
  CalendarIcon,
  CashIcon,
  CertificateIcon,
  ChevronNextIcon,
  ClockOutlineIcon,
  ClosedCaptioningIcon,
  CollectionIcon,
  DegreeIcon,
  GlobeIcon,
  GradingIcon,
  HonorsAssignmentIcon,
  InstitutionIcon,
  VersionHistoryIcon,
} from '@coursera/cds-icons';

import type { S12nGlanceItem } from 'bundles/enterprise-legacy-learner-home/types/xdpTypes';

// Icons supported by this component
const ALL_ICONS: { [key: string]: React.ComponentType<IconProps> } = {
  BarChartOneFillIcon,
  BarChartTwoFillIcon,
  BarChartThreeFillIcon,
  CertificateIcon,
  GlobeIcon,
  CashIcon,
  ClockOutlineIcon,
  CollectionIcon,
  InstitutionIcon,
  GradingIcon,
  ClosedCaptioningIcon,
  ChevronNextIcon,
  HonorsAssignmentIcon,
  CalendarIcon,
  VersionHistoryIcon,
  DegreeIcon,
};

const DEFAULT_ICON = ChevronNextIcon;

export type ProductGlanceList = Array<S12nGlanceItem>;

export type ProductGlanceProps = {
  dataList: ProductGlanceList;
  iconColor?: string;
  rootClassName?: string;
};

const GlanceIcon = ({ Svg }: { className?: string; iconColor?: string; Svg: React.ComponentType<IconProps> }) => {
  return (
    <div
      css={css`
        width: 44px;
        height: 44px;
        border-radius: 50%;
        border: 2px solid var(--cds-color-grey-300);
        display: flex;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
      `}
    >
      <Svg size="medium" />
    </div>
  );
};

export default function ProductGlance({ dataList = [], rootClassName }: ProductGlanceProps) {
  if (!dataList.length) {
    return null;
  }

  const dataListWithIcons = dataList.map((item) => ({
    ...item,
    IconTag: ALL_ICONS[item.icon] || DEFAULT_ICON,
  }));

  return (
    <div className={classNames('ProductGlance', rootClassName)}>
      {dataListWithIcons.map(({ title, subtitle, IconTag, hasBottomBorder }, index) => (
        <Grid
          container
          key={typeof title === 'string' ? title : index}
          css={
            hasBottomBorder &&
            css`
              border-bottom: solid 1px var(--cds-color-grey-50);
            `
          }
        >
          <Grid item>
            <GlanceIcon
              Svg={IconTag}
              css={css`
                margin: 0 var(--cds-spacing-200) var(--cds-spacing-100) var(--cds-spacing-100);
              `}
            />
          </Grid>
          <Grid item>
            <Typography2
              component="div"
              variant="subtitleMedium"
              css={css`
                margin-bottom: 0;
                margin-top: ${!subtitle && 'var(--cds-spacing-100)'};
              `}
            >
              {title}
            </Typography2>
            {subtitle && (
              <Typography2 component="p" variant="bodySecondary" color="supportText">
                {subtitle}
              </Typography2>
            )}
          </Grid>
        </Grid>
      ))}
    </div>
  );
}
